<template>
  <div
    v-if="hasFolders || isHKlandUser"
    class="submissions__topbar"
  >
    <HKLLeftRight>
      <template
        #left
        v-if="hasFolders"
      >
        <HKLButton
          v-if="!isAllCollapsed"
          color="orange"
          @click="() => collapseAll()"
        >
          {{ $t('collapseAll') }}
        </HKLButton>
        <HKLButton
          v-else
          color="darkblue"
          @click="() => expandAll()"
        >
          {{ $t('expandAll') }}
        </HKLButton>
      </template>
      <template
        #right
        v-if="isHKlandUser"
      >
        <Sticky
          align="right"
          :stickyTop="40"
          specifyWidth="auto"
        >
          <HKLButton
            v-if="!isStarted && canStartGreenFitout"
            color="orange"
            @click="openAddFoldersModal"
          >
            {{ $t('tenantProjects.greenFitOut.allowStart') }}
          </HKLButton>
          <template v-if="isStarted">
            <HKLButton
              v-if="!isCompleted && canOnHold"
              color="darkblue"
              action="onHold"
              :disabled="submission.onHold"
              @click="() => openOnHoldModal()"
            />
            <template v-if="isUnderReview">
              <HKLButton
                v-if="canCommentOnGreenFitout && isCurrentReviewer"
                color="darkblue"
                action="cancelLetter"
                @click="openCancelModal"
              />
              <HKLButton
                v-if="canCommentOnGreenFitout && isCurrentReviewer"
                color="darkblue"
                action="save"
                @click="() => saveLetter()"
              />
              <HKLButton
                v-if="canPreview"
                color="orange"
                :action="canSend ? 'preview_send' : 'preview'"
                @click="previewSend"
              />
              <HKLButton
                v-if="canCommentOnGreenFitout || canSend"
                color="orange"
                action="nextReviewer"
                @click="nextReviewers"
              />
            </template>
            <HKLButton
              v-if="isDraft && canStartGreenFitout"
              color="orange"
              @click="openAddFoldersModal"
            >
              {{ $t('tenantProjects.greenFitOut.addFolder') }}
            </HKLButton>
          </template>
        </Sticky>
      </template>
    </HKLLeftRight>
    <AddFolders ref="addFoldersRef" />
    <HKLModal
      ref="cancelModal"
      :title="$t('tenantProjects.greenFitOut.cancelAlert')"
      okBtnAction="yes"
      cancelBtnAction="no"
      @onOkClick="cancelLetter"
    />
    <GreenFitOutOnHoldModal
      ref="onHoldModal"
    />
    <GreenFitOutNextReviewModal
      ref="nextReviewModal"
    />
    <GreenFitOutPreviewSendModal
      v-if="isUnderReview"
      ref="previewSendModal"
      :previewData="previewData"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { unref, ref, computed } from 'vue-demi'
import Sticky from '@/controls/Sticky'
import HKLButton from '@/controls/Button/HKLButton'
import HKLModal from '@/controls/Modal/HKLModal'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import GreenFitOutOnHoldModal from './GreenFitOutOnHoldModal.vue'
import GreenFitOutNextReviewModal from './GreenFitOutNextReviewModal.vue'
import GreenFitOutPreviewSendModal from './GreenFitOutPreviewSendModal.vue'
import { useStoreGetters } from '@/store'
import { updateGreenFitoutSubmission } from '@/api/tenantProjects/greenFitout.request'
import { GREEN_FITOUT_STATUS } from './consts'
import AddFolders from './AddFolders'
import {
  useRefreshCurrentGreenFitout,
  useCurrentGreenFitout,
  useCurrentGreenFitoutStatus,
  useGreenFitoutPerms,
  useGreenFitoutCurrentReviewer,
  useFoldersCollapseToggleEvent,
} from './hook'
import { useRefModal } from '../../../controls/Modal/modal.hook'
import { utils as formUtils } from '@/plugin/InputChange'

export default {
  components: {
    HKLButton,
    HKLModal,
    HKLLeftRight,
    Sticky,
    GreenFitOutOnHoldModal,
    GreenFitOutNextReviewModal,
    AddFolders,
    GreenFitOutPreviewSendModal,
  },
  setup () {
    const { modal: addFoldersRef, open: openAddFoldersModal } = useRefModal()
    const { modal: cancelModal, open: openCancelModal } = useRefModal()
    const { modal: onHoldModal, open: openOnHoldModal } = useRefModal()
    const { modal: nextReviewModal, open: openNextReviewModal } = useRefModal()
    const { modal: previewSendModal, open: openPreviewSendModal } = useRefModal()
    const userStore = useStoreGetters('user', ['isTenant', 'isEngineer', 'isHKlandUser'])
    const { isUnderReview, isCompleted, isStarted, isDraft } = useCurrentGreenFitoutStatus()
    const { canPreview, canSend, canStartGreenFitout, canOnHold, canCommentOnGreenFitout } = useGreenFitoutPerms()

    const submission = useCurrentGreenFitout()
    const hasFolders = computed(() => (unref(submission).folders || []).length !== 0)
    const refresh = useRefreshCurrentGreenFitout()
    const { isCurrentReviewer } = useGreenFitoutCurrentReviewer()
    const {
      expandAll,
      collapseAll,
      isAllCollapsed,
    } = useFoldersCollapseToggleEvent()

    const saveLetter = async (isSilent) => {
      await updateGreenFitoutSubmission(
        unref(submission).id,
        lodash.pick(unref(submission), ['comments', 'lock']),
        isSilent,
      )
      formUtils.resetDirty()
      await refresh()
    }
    const cancelLetter = async () => {
      await updateGreenFitoutSubmission(submission.value.id, {
        status: GREEN_FITOUT_STATUS.DRAFT,
        lock: submission.value.lock,
      })
      formUtils.resetDirty()
      await refresh()
    }
    const previewData = ref(null)
    const nextReviewers = async () => {
      await saveLetter(true)
      openNextReviewModal()
    }

    return {
      previewData,
      submission,
      hasFolders,
      previewSend: async () => {
        await saveLetter(true)
        previewData.value = submission
        openPreviewSendModal()
      },
      ...userStore,
      previewSendModal,
      canCommentOnGreenFitout,
      canStartGreenFitout,
      canOnHold,
      canPreview,
      canSend,
      addFoldersRef,
      openAddFoldersModal,
      cancelModal,
      openCancelModal,
      onHoldModal,
      openOnHoldModal,
      nextReviewModal,
      isUnderReview,
      isCompleted,
      isStarted,
      isDraft,
      isCurrentReviewer,
      saveLetter,
      cancelLetter,
      nextReviewers,
      isAllCollapsed,
      expandAll,
      collapseAll,
    }
  },
}
</script>
<style lang="scss" scoped>
.submissions__topbar .hkl-left-right {
  width: 100%;
}
</style>
